/**
 * apps/admin/src/lib/intercom.ts · Intercom Messenger 接入（单点入口）
 *
 * 承接：
 * - main.tsx 三方 SDK 接入范式（Sentry / PostHog：env 缺省即跳过、只在一处 init）
 * - Intercom 官方 JS 安装片段（widget.intercom.io/widget/<app_id> + intercomSettings）
 *
 * 说明：
 * - app_id 默认取工作区 `t5excx1y`，可用 `VITE_INTERCOM_APP_ID` 覆盖；置空字符串即整体关闭。
 * - Admin Console 没有登录态，默认以**固定测试人设**（王芳）身份 boot，而不是匿名访客。
 *   原因：Fin 的 Shopify 连接器 `Get order info` 请求 URL 里写死了
 *   `/customers/{{custom_data.shopify_id}}/orders.json`，匿名访客身上没有 shopify_id，
 *   订单类问题必然取不到数；带上已被 Shopify app 富化过的联系人才走得通。
 * - 换人 / 回匿名（改完刷新页面即可，不用重新 build）：
 *     localStorage.setItem("cs_admin_intercom_user", JSON.stringify({ user_id: "…", name: "…", email: "…" }))
 *     localStorage.setItem("cs_admin_intercom_user", "anonymous")  // 强制匿名访客
 *     localStorage.removeItem("cs_admin_intercom_user")            // 回到默认人设
 * - E2E（Playwright）下不启动：悬浮 launcher 会挡住右下角元素、且不应在 CI 打外网。
 */

const APP_ID = import.meta.env.VITE_INTERCOM_APP_ID ?? "t5excx1y";
const API_BASE = "https://api-iam.intercom.io";
const IDENTITY_STORAGE_KEY = "cs_admin_intercom_user";
/** localStorage 写这个值 = 强制匿名访客（用来对比「有身份 / 没身份」两种口径） */
const ANONYMOUS_SENTINEL = "anonymous";

/**
 * 默认固定人设 · Intercom 联系人「芳 王」（workspace 里的 seed 用户）
 *
 * 该联系人已被 Shopify app 富化：Shopify domain=text-1zcl8ul8.myshopify.com /
 * Shopify id=9780157972671 / 3 笔订单 / 最近一单 #1007。user_id 与 Shopify customer id 同值，
 * boot 时按 user_id 命中既有联系人，Fin 才能拿到 custom_data.shopify_id 去查订单。
 */
const DEFAULT_IDENTITY: Record<string, unknown> = {
	user_id: "9780157972671",
	name: "芳 王",
	email: "wangfang.vip@example.com",
};

type IntercomFn = {
	(...args: unknown[]): void;
	q?: unknown[][];
	c?: (args: unknown[]) => void;
};

declare global {
	interface Window {
		Intercom?: IntercomFn;
		intercomSettings?: Record<string, unknown>;
	}
}

/** 身份：localStorage 覆盖 > 默认固定人设；写 "anonymous" 则回匿名访客 */
function readIdentity(): Record<string, unknown> {
	try {
		const raw = window.localStorage.getItem(IDENTITY_STORAGE_KEY);
		if (!raw) return DEFAULT_IDENTITY;
		if (raw.trim() === ANONYMOUS_SENTINEL) return {};
		const parsed: unknown = JSON.parse(raw);
		return parsed && typeof parsed === "object"
			? (parsed as Record<string, unknown>)
			: DEFAULT_IDENTITY;
	} catch {
		return DEFAULT_IDENTITY;
	}
}

function isEnabled(): boolean {
	if (typeof window === "undefined") return false;
	if (!APP_ID) return false;
	// Playwright / 自动化浏览器不启动
	if (navigator.webdriver) return false;
	return true;
}

function injectWidgetScript(): void {
	const stub: IntercomFn = (...args: unknown[]) => {
		stub.c?.(args);
	};
	stub.q = [];
	stub.c = (args: unknown[]) => {
		stub.q?.push(args);
	};
	window.Intercom = stub;

	const load = () => {
		const script = document.createElement("script");
		script.type = "text/javascript";
		script.async = true;
		script.src = `https://widget.intercom.io/widget/${APP_ID}`;
		document.head.appendChild(script);
	};

	if (document.readyState === "complete") load();
	else window.addEventListener("load", load, false);
}

/** 启动（或以最新 settings 复用已有实例）· 幂等 */
export function bootIntercom(): void {
	if (!isEnabled()) return;

	window.intercomSettings = {
		api_base: API_BASE,
		app_id: APP_ID,
		...readIdentity(),
	};

	if (typeof window.Intercom === "function") {
		window.Intercom("reattach_activator");
		window.Intercom("update", window.intercomSettings);
		return;
	}

	injectWidgetScript();
}

/** SPA 路由切换后通知 Intercom（官方要求：换页要 update，否则收不到新会话推送） */
export function updateIntercomOnRouteChange(): void {
	if (!isEnabled()) return;
	window.Intercom?.("update", { last_request_at: Math.floor(Date.now() / 1000) });
}
